/**
 * Recurring Transaction Add/Edit Modal
 * 
 * Form modal for creating or editing recurring transaction templates.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { cn } from '../../lib/utils';
import type { RecurringTransaction, RecurringTransactionFormData, RecurringFrequency, RecurringTransactionStatus } from '../../types/recurring-transaction';
import type { ModeType } from '../../types';
import { getRecurringTransactionCategories } from '../../data/recurring-transactions-data';

interface RecurringTransactionModalProps {
  transaction: RecurringTransaction | null;
  onSave: (transaction: RecurringTransaction, formData?: RecurringTransactionFormData) => void;
  onClose: () => void;
  isSaving?: boolean;
  mode?: ModeType;
}

export function RecurringTransactionModal({ transaction, onSave, onClose, isSaving, mode }: RecurringTransactionModalProps) {
  const isEditing = transaction !== null;
  const categories = getRecurringTransactionCategories();

  const [formData, setFormData] = useState<RecurringTransactionFormData>({
    name: transaction?.name || '',
    direction: transaction?.direction || 'outflow',
    amount: transaction?.amount?.toString() || '',
    isVariable: transaction?.isVariable || false,
    frequency: transaction?.frequency || 'Monthly',
    startDate: transaction?.startDate ? formatDateForInput(transaction.startDate) : '',
    endDate: transaction?.endDate ? formatDateForInput(transaction.endDate) : '',
    category: transaction?.category || '',
    counterpartyName: transaction?.counterpartyName || '',
    status: transaction?.status || 'Active',
    notes: transaction?.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Format date for input field (YYYY-MM-DD)
  function formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Calculate next due date based on start date and frequency
  function calculateNextDueDate(startDate: Date, freq: RecurringFrequency): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const next = new Date(startDate);

    const freqMap: Record<RecurringFrequency, { base: string; n: number }> = {
      Daily: { base: "daily", n: 1 },
      Weekly: { base: "weekly", n: 1 },
      Fortnightly: { base: "weekly", n: 2 },
      Monthly: { base: "monthly", n: 1 },
      Quarterly: { base: "monthly", n: 3 },
      Annual: { base: "yearly", n: 1 },
    };
    const { base, n } = freqMap[freq];

    while (next <= today) {
      switch (base) {
        case 'daily':
          next.setDate(next.getDate() + n);
          break;
        case 'weekly':
          next.setDate(next.getDate() + 7 * n);
          break;
        case 'monthly':
          next.setMonth(next.getMonth() + n);
          break;
        case 'yearly':
          next.setFullYear(next.getFullYear() + n);
          break;
      }
    }
    
    return next;
  }

  // Validate form
  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    // Amount validation
    if (!formData.isVariable) {
      if (!formData.amount) {
        newErrors.amount = 'Amount is required (or check Variable)';
      } else {
        const amount = parseFloat(formData.amount);
        if (isNaN(amount) || amount <= 0) {
          newErrors.amount = 'Amount must be a positive number';
        }
      }
    }

    // Frequency validation
    if (!formData.frequency) {
      newErrors.frequency = 'Frequency is required';
    }

    // Start date validation
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const startDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate < today && !isEditing) {
        newErrors.startDate = 'Start date cannot be in the past';
      }
    }

    // Category validation
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Derived: all required fields complete (for button state)
  const isFormValid =
    formData.name.trim().length > 0 &&
    formData.name.trim().length <= 100 &&
    (formData.isVariable || (!!formData.amount && parseFloat(formData.amount) > 0)) &&
    !!formData.frequency &&
    !!formData.startDate &&
    !!formData.category;

  // Handle form submission
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const startDate = new Date(formData.startDate);
    const nextDueDate = calculateNextDueDate(startDate, formData.frequency);

    const savedTransaction: RecurringTransaction = {
      id: transaction?.id || `rt-${Date.now()}`,
      name: formData.name.trim(),
      direction: formData.direction,
      amount: formData.isVariable ? null : parseFloat(formData.amount),
      isVariable: formData.isVariable,
      frequency: formData.frequency,
      startDate,
      endDate: formData.endDate ? new Date(formData.endDate) : undefined,
      nextDueDate,
      category: formData.category,
      counterpartyName: formData.counterpartyName?.trim() || undefined,
      status: formData.status,
      notes: formData.notes?.trim(),
      createdAt: transaction?.createdAt || new Date(),
      updatedAt: new Date(),
      dataSourceName: transaction?.dataSourceName ?? 'Manual Entry - Recurring Transactions',
    };

    onSave(savedTransaction, formData);
  }

  function handleChange(field: keyof RecurringTransactionFormData, value: string | boolean) {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Recurring Transaction' : 'Add Recurring Transaction'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the details of this recurring transaction template'
              : 'Create a new template for transactions that repeat on a regular schedule'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Office Rent, PAYE Payment, Software Subscription"
              maxLength={100}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Direction */}
          <div className="space-y-2">
            <Label htmlFor="direction">
              Direction <span className="text-destructive">*</span>
            </Label>
            <Select value={formData.direction} onValueChange={(value: string) => handleChange('direction', value)}>
              <SelectTrigger id="direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outflow">Outflow (Payment)</SelectItem>
                <SelectItem value="inflow">Inflow (Receipt)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount {!formData.isVariable && <span className="text-destructive">*</span>}
            </Label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                    £
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    disabled={formData.isVariable}
                  />
                </div>
                {errors.amount && (
                  <p className="text-sm text-destructive mt-1">{errors.amount}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isVariable"
                  checked={formData.isVariable}
                  onCheckedChange={(checked) => handleChange('isVariable', checked)}
                />
                <Label htmlFor="isVariable" className="cursor-pointer">
                  Variable amount
                </Label>
              </div>
            </div>
          </div>

          {/* Frequency and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frequency">
                Frequency <span className="text-destructive">*</span>
              </Label>
              <Select value={formData.frequency} onValueChange={(value: RecurringFrequency) => handleChange('frequency', value)}>
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Daily">Daily</SelectItem>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Annual">Annual</SelectItem>
                </SelectContent>
              </Select>
              {errors.frequency && (
                <p className="text-sm text-destructive">{errors.frequency}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-destructive">*</span>
              </Label>
              <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category}</p>
              )}
            </div>
          </div>

          {/* Start Date and End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Start Date <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate
                      ? format(parse(formData.startDate, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
                      : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.startDate && isValid(parse(formData.startDate, 'yyyy-MM-dd', new Date()))
                      ? parse(formData.startDate, 'yyyy-MM-dd', new Date())
                      : undefined}
                    onSelect={(date) => handleChange('startDate', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.startDate && (
                <p className="text-sm text-destructive">{errors.startDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>End Date (Optional)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formData.endDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.endDate
                      ? format(parse(formData.endDate, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
                      : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.endDate && isValid(parse(formData.endDate, 'yyyy-MM-dd', new Date()))
                      ? parse(formData.endDate, 'yyyy-MM-dd', new Date())
                      : undefined}
                    onSelect={(date) => handleChange('endDate', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Leave blank for ongoing</p>
            </div>
          </div>

          {/* Counterparty Name */}
          <div className="space-y-2">
            <Label htmlFor="counterpartyName">Counterparty Name (Optional)</Label>
            <Input
              id="counterpartyName"
              value={formData.counterpartyName}
              onChange={(e) => handleChange('counterpartyName', e.target.value)}
              placeholder="e.g., HMRC, Landlord Ltd, British Gas"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value: string) => handleChange('status', value)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Paused">Paused</SelectItem>
                <SelectItem value="Ended">Ended</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional details or reminders..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !isFormValid}>
              {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Transaction'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
